/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef } from 'react';
import { Link } from '@core/Link';
import { useA11y } from '@context/Utils';
import { appIconsPath, appLinkSize, appLinkStyle } from '@utils/globalConstant';
import './TariffProduct.scss';
import PropTypes from 'prop-types';
import { useStaticContent } from '@dom-digital-online-media/dom-static-content-sdk';
import Ta11yText from '@core/Ta11yText';
import { useActivation } from '@context/MobileOne';

export function TariffProduct({
  tariff,
  TariffBannerIcon,
  showActiveTariffBanner = false,
  isActiveTariff = true,
  // showBanner = false,
  // bannerText = '',
  customClass = '',
  tariffPdf,
  isActiveCard
}) {
  // Context
  const { staticContentData } = useStaticContent();
  const { getTariffName } = useActivation();
  const { tA11yTranslate } = useA11y();

  // States
  const [pdfLink, setPdfLink] = React.useState(tariffPdf);
  const tariffName = getTariffName(tariff.name, tariff.nameSpeed);

  const bannerText =
    tariff.additionalInfo.ribbon && tariff.additionalInfo.ribbon.copyPublic
      ? tariff.additionalInfo.ribbon.copyPublic
      : '';

  // Hooks
  useEffect(() => {
    if (!tariffPdf && staticContentData && tariff) {
      const pdfData = staticContentData?.ek_pdfPib?.listRedirection;
      const findPdf = pdfData.find((data) => data?.tariffID === tariff?.id);
      if (findPdf?.redirectionURL) setPdfLink(findPdf?.redirectionURL);
    }
  }, []);

  return (
    <div id={`tariff${tariff.id}`} className={`tariff-product ${customClass}`}>
      <div>
        <div
          className="card rounded-4 border-3"
          style={{ borderColor: tariff.additionalInfo.primaryColor }}>
          {/* Header */}
          <div className="card-header border-0 p-0">
            {showActiveTariffBanner && (
              <div className="py-12 px-16 d-flex align-items-center justify-content-center bg-white rounded-top-4 gap-8">
                <img
                  src={TariffBannerIcon || isActiveTariff ? appIconsPath.CHECK : appIconsPath.PAUSE}
                  className="link-icon ps-8"
                  height={16}
                  alt=""
                />
                {/* BFSG: remove hardcoded text after new CMS keys are ready */}
                <p className="ek-copy-sm-14-semibold text-primary">
                  {isActiveTariff ? 'Aktiver Tarif' : 'Pausierter Tarif'}
                </p>
              </div>
            )}
            <div
              className={`tariff-card-top-wrapper py-8 text-center 
                    ${!isActiveTariff && 'opacity-50'} 
                    ${!showActiveTariffBanner && 'rounded-top-3'}
                  `}
              style={{ backgroundColor: tariff.additionalInfo.primaryColor }}>
              <Ta11yText
                tag="h3"
                textContent={tariffName}
                className="tariff-card-name ek-copy-16-semibold mb-0 text-primary"
              />
              <div className="tariff-card-data-volume d-flex justify-content-center align-items-baseline gap-12">
                {tariff.strikeGB && (
                  <Ta11yText
                    tag="span"
                    textContent={tA11yTranslate(tariff.strikeGB)}
                    className="text-primary ek-h2 mb-8"
                  />
                )}
                <Ta11yText
                  tag="p"
                  textContent={tA11yTranslate(tariff.header)}
                  className="ek-special-tariffcard-data text-primary"
                />
              </div>
            </div>
          </div>
          {/* Body */}
          <ul
            className={`list-unstyled mb-0 bg-white rounded-bottom-4 ${
              !isActiveTariff && 'opacity-50'
            }`}>
            {bannerText && (
              <li
                className={`py-4 text-center ${!isActiveTariff && 'opacity-50'}`}
                style={{
                  backgroundColor: tariff.additionalInfo?.ribbon?.bgColorPublic || '#88c2eb', // ek-midblue
                  color: tariff.additionalInfo?.ribbon?.fontColor
                }}>
                <Ta11yText
                  tag="span"
                  textContent={tA11yTranslate(bannerText)}
                  className="ek-copy-sm-14-semibold text-primary"
                />
              </li>
            )}
            {tariff.bullets.map((bullet, index) => (
              <li
                key={`${index}`}
                className={`d-flex gap-12 mb-12 px-16 ${index === 0 ? 'pt-16' : ''}`}>
                <div>
                  <img src={appIconsPath.CHECK} className="link-icon" height={16} alt="" />
                </div>
                <div>
                  <Ta11yText
                    tag="span"
                    textContent={tA11yTranslate(bullet.bullet)}
                    className="ek-copy-sm-14-regular text-primary"
                  />
                </div>
              </li>
            ))}
            <div className="border border-ek-midblue mt-0 mb-12 mx-16" />
            <li className="px-16 pb-16">
              <div className="d-flex align-items-baseline gap-4">
                <Ta11yText
                  tag="span"
                  textContent={tA11yTranslate(tariff.price)}
                  className="ek-h2 text-primary m-0 p-0"
                />
                <span className="ek-copy-sm-14-regular text-primary">
                  / <Ta11yText tag="span" textContent={tA11yTranslate(tariff.duration)} />
                </span>
              </div>
              {tariff.strikePrice && (
                <Ta11yText
                  tag="span"
                  textContent={tA11yTranslate(tariff.strikePrice)}
                  className="ek-copy-16-semibold text-primary"
                />
              )}
            </li>
          </ul>
        </div>
      </div>
      <div>
        <div className="mt-12 text-center">
          <Link
            label="ek_reg_pdf_lnk1"
            iconRight={appIconsPath.ARROW_RIGHT_SMALL}
            linkStyle={appLinkStyle.PRIMARY}
            linkSize={appLinkSize.SMALL}
            href={pdfLink}
            tabIndex={isActiveCard ? 0 : -1} // Focusable only if active
            aria-hidden={!isActiveCard} // Hidden from screen readers if not active
          />
        </div>
      </div>
    </div>
  );
}

TariffProduct.propTypes = {
  id: PropTypes.number,
  tariff: PropTypes.shape({}),
  isActiveTariff: PropTypes.bool,
  showBanner: PropTypes.bool,
  customClass: PropTypes.string,
  tariffPdf: PropTypes.string
};

export default TariffProduct;
